import { useState, useEffect, useRef } from 'react';
import { isValidYouTubeThumbnail, getSanitizedYouTubeThumbnail, isValidYouTubeVideoId } from '../utils/validation';
import { useFeatureFlag, trackFlagInteraction } from '../utils/featureFlags';
import type { ArtistSummary, Playlist, VideoResult } from '../types';
import { useI18n } from '../i18n/useI18n';

interface Props {
  open: boolean;
  onClose: () => void;
  quickSong: string;
  setQuickSong: (v: string) => void;
  ytResults: VideoResult[];
  isSearching: boolean;
  ytError: string | null;
  onPreview: (v: VideoResult) => void;
  onAdd: (v: VideoResult) => void;
  singerName?: string;
  setSingerName?: (v: string) => void;
  isAuthenticated?: boolean;
  favorites?: VideoResult[];
  savedSongs?: VideoResult[];
  playlists?: Playlist[];
  onToggleFavorite?: (v: VideoResult, isFavorite: boolean) => void;
  isAdding?: boolean;
  resultsFromCache?: boolean;
  onForceYouTubeSearch?: () => void;
  forceDisabled?: boolean;
  artists?: ArtistSummary[];
  selectedArtist?: string | null;
  artistSongs?: VideoResult[] | null;
  isLoadingArtistSongs?: boolean;
  onSelectArtist?: (artist: string) => void;
  onClearArtist?: () => void;
}

type Tab = 'search' | 'playlists' | 'saved';

function PlaylistHeader({
  playlist,
  searchOpen,
  searchQuery,
  onToggle,
  onChange,
  t,
}: {
  playlist: Playlist;
  searchOpen: boolean;
  searchQuery: string;
  onToggle: () => void;
  onChange: (value: string) => void;
  t: (key: string) => string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{playlist.name}</div>
        <button
          type="button"
          className="btn-secondary"
          style={{
            width: 32,
            height: 32,
            padding: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={onToggle}
          aria-label={t('common.search')}
          title={t('common.search')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>
      {searchOpen && (
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('common.search')}
          style={{ width: '100%' }}
          autoFocus
        />
      )}
    </div>
  );
}

function VideoCardHorizontal({
  video,
  isAuthenticated,
  isFav,
  onToggleFavorite,
  onPreview,
  onAdd,
  compact = false,
  isAdding = false,
}: {
  video: VideoResult;
  isAuthenticated?: boolean;
  isFav?: boolean;
  onToggleFavorite?: (v: VideoResult, next: boolean) => void;
  onPreview: (v: VideoResult) => void;
  onAdd: (v: VideoResult) => void;
  compact?: boolean;
  isAdding?: boolean;
}) {
  const { t } = useI18n();
  const getSafeThumbnail = (video: VideoResult): string => {
    if (isValidYouTubeThumbnail(video.thumbnail)) return video.thumbnail;
    if (isValidYouTubeVideoId(video.videoId)) {
      return getSanitizedYouTubeThumbnail(video.videoId, 'mqdefault') || '';
    }
    return '';
  };

  return (
    <div
      className={`yt-card-horizontal ${compact ? 'yt-card-horizontal--compact' : ''}`}
      style={{
        background: 'var(--bg)',
        border: '1px solid var(--border)',
        scrollSnapAlign: 'start',
      }}
    >
      <div className="yt-card-horizontal__thumb">
        <img
          src={getSafeThumbnail(video)}
          alt={video.title}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
      <div className="yt-card-horizontal__info">
        <div
          className="yt-card-horizontal__title"
          title={video.song ?? video.title}
        >
          {video.song ?? video.title}
        </div>
        {video.song && video.artist && (
          <div className="yt-card-horizontal__artist" title={video.artist}>
            {video.artist}
          </div>
        )}
        <div className="yt-card-horizontal__actions">
          <IconButton className="btn-secondary" icon="▶" label={t('search.preview')} onClick={() => onPreview(video)} size="small" />
          {isAuthenticated && (
            <IconButton
              className={isFav ? 'btn-primary' : 'btn-secondary'}
              icon={isFav ? '★' : '☆'}
              label={isFav ? t('search.removeFavorite') : t('search.addFavorite')}
              onClick={() => onToggleFavorite?.(video, !isFav)}
              size="small"
            />
          )}
          <IconButton
            className="btn-primary"
            icon={isAdding ? '⏳' : '＋'}
            label={isAdding ? t('search.adding') : t('search.add')}
            onClick={() => onAdd(video)}
            size="small"
          />
        </div>
      </div>
    </div>
  );
}

function IconButton({
  className,
  onClick,
  icon,
  label,
  size = 'default',
}: {
  className: string;
  onClick: () => void;
  icon: string;
  label: string;
  size?: 'default' | 'small';
}) {
  return (
    <button
      className={`icon-btn ${className}`}
      onClick={onClick}
      aria-label={label}
      title={label}
      style={
        size === 'small'
          ? { width: 44, height: 44, padding: 0, flex: 'none' }
          : undefined
      }
    >
      {icon}
    </button>
  );
}

function ArtistCard({
  artist,
  onSelect,
}: {
  artist: ArtistSummary;
  onSelect: (artist: string) => void;
}) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const initial = artist.artist.trim().charAt(0).toUpperCase();
  const photo = artist.image || artist.thumbnail;
  const showThumb = !!photo && !thumbFailed;
  return (
    <button type="button" className="artist-card" onClick={() => onSelect(artist.artist)}>
      <span className="artist-card__avatar">
        {showThumb ? (
          <img src={photo} alt="" onError={() => setThumbFailed(true)} />
        ) : (
          initial
        )}
      </span>
      <span className="artist-card__name" title={artist.artist}>
        {artist.artist}
      </span>
      <span className="artist-card__count">
        {artist.songCount} songs
      </span>
    </button>
  );
}

function VideoCard({
  video,
  isAuthenticated,
  isFav,
  onToggleFavorite,
  onPreview,
  onAdd,
  compact = false,
  isAdding = false,
}: {
  video: VideoResult;
  isAuthenticated?: boolean;
  isFav?: boolean;
  onToggleFavorite?: (v: VideoResult, next: boolean) => void;
  onPreview: (v: VideoResult) => void;
  onAdd: (v: VideoResult) => void;
  compact?: boolean;
  isAdding?: boolean;
}) {
  const { t } = useI18n();
  const getSafeThumbnail = (video: VideoResult): string => {
    if (isValidYouTubeThumbnail(video.thumbnail)) return video.thumbnail;
    if (isValidYouTubeVideoId(video.videoId)) {
      return getSanitizedYouTubeThumbnail(video.videoId, 'mqdefault') || '';
    }
    return '';
  };

  return (
    <div
      className="yt-card"
      style={{
        flexShrink: 0,
        width: compact ? 'min(52vw, 160px)' : 'min(72vw, 220px)',
        background: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
        scrollSnapAlign: 'start',
        position: 'relative',
      }}
    >
      <div style={{ width: '100%', paddingTop: '56.25%', position: 'relative' }}>
        <img
          src={getSafeThumbnail(video)}
          alt={video.title}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
      <div style={{ padding: 12 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            minHeight: 40,
            overflow: 'hidden',
            lineHeight: 1.35,
            marginBottom: 10,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
          title={video.song ?? video.title}
        >
          {video.song ?? video.title}
        </div>
        {video.song && video.artist && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              marginTop: -4,
              marginBottom: 10,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={video.artist}
          >
            {video.artist}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <IconButton className="btn-secondary" icon="▶" label={t('search.preview')} onClick={() => onPreview(video)} />
          <IconButton
            className="btn-primary"
            icon={isAdding ? '⏳' : '＋'}
            label={isAdding ? t('search.adding') : t('search.add')}
            onClick={() => onAdd(video)}
          />
          {isAuthenticated && (
            <IconButton
              className={isFav ? 'btn-primary' : 'btn-secondary'}
              icon={isFav ? '★' : '☆'}
              label={isFav ? t('search.removeFavorite') : t('search.addFavorite')}
              onClick={() => onToggleFavorite?.(video, !isFav)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function SearchSheet({
  open,
  onClose,
  quickSong,
  setQuickSong,
  ytResults,
  isSearching,
  ytError,
  onPreview,
  onAdd,
  singerName,
  setSingerName,
  isAuthenticated,
  favorites = [],
  savedSongs = [],
  playlists = [],
  onToggleFavorite,
  isAdding = false,
  resultsFromCache = false,
  onForceYouTubeSearch,
  forceDisabled = false,
  artists = [],
  selectedArtist = null,
  artistSongs = null,
  isLoadingArtistSongs = false,
  onSelectArtist,
  onClearArtist,
}: Props) {
  const { t } = useI18n();
  // Abre expandida por defeito: no evento real todo o mundo a expandia à mão.
  // Tocar no cabeçalho (ou arrastar) colapsa para a versão compacta.
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('search');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [searchVisibleCount, setSearchVisibleCount] = useState(15);
  const [playlistCarouselIndex, setPlaylistCarouselIndex] = useState(0);
  const [playlistSearch, setPlaylistSearch] = useState<Record<string, string>>({});
  const [playlistSearchOpen, setPlaylistSearchOpen] = useState<Record<string, boolean>>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  const playlistCarouselRef = useRef<HTMLDivElement>(null);
  const addSongLayout = useFeatureFlag('kb');
  const useFocusAwareLayout = addSongLayout === 'new';

  // Secção de artistas partilhada: no topo quando não há resultados, no fim
  // da lista (antes do botão "search on youtube") quando há pesquisa.
  const artistRow = artists.length > 0 && onSelectArtist ? (
    <div style={{ marginTop: 4 }}>
      <div className="artist-row__title">{t('search.artists')}</div>
      <div className="artist-row">
        {artists.map((a) => (
          <ArtistCard key={a.artist} artist={a} onSelect={onSelectArtist} />
        ))}
      </div>
    </div>
  ) : null;

  // Drag-to-expand/collapse: live offset applied as translateY while dragging,
  // snap on release. Positive = dragging down (collapse), negative = up (expand).
  const dragStateRef = useRef<{ startY: number; offset: number; dragging: boolean } | null>(null);
  const dragMovedRef = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const DRAG_THRESHOLD = 60;

  const onDragStart = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Don't capture drags that start on interactive elements (e.g. close button),
    // otherwise pointer capture would swallow their click.
    if ((e.target as HTMLElement).closest('button, input, a')) return;
    dragStateRef.current = { startY: e.clientY, offset: 0, dragging: true };
    dragMovedRef.current = false;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onDragMove = (e: React.PointerEvent) => {
    const drag = dragStateRef.current;
    if (!drag?.dragging) return;
    const delta = e.clientY - drag.startY;
    if (Math.abs(delta) > 8) dragMovedRef.current = true;
    // Clamp: don't drag beyond the sheet's useful range in either direction.
    const clamped = Math.max(-window.innerHeight * 0.4, Math.min(window.innerHeight * 0.6, delta));
    drag.offset = clamped;
    setDragOffset(clamped);
  };

  const onDragEnd = () => {
    const drag = dragStateRef.current;
    if (!drag?.dragging) return;
    drag.dragging = false;
    const offset = drag.offset;
    dragStateRef.current = null;
    setDragOffset(0);
    if (!dragMovedRef.current) return; // plain click, not a drag
    if (offset <= -DRAG_THRESHOLD) {
      setExpanded(true);
    } else if (offset >= DRAG_THRESHOLD) {
      setExpanded(false);
    }
  };

  const LIST_INITIAL_LIMIT = 15;

  const getPlaylistSongs = (playlist: Playlist) => {
    const query = (playlistSearch[playlist.id] || '').trim().toLowerCase();
    return query ? playlist.songs.filter((s) => s.title.toLowerCase().includes(query)) : playlist.songs;
  };

  useEffect(() => {
    if (!open) {
      setExpanded(false);
      setActiveTab('search');
      setIsInputFocused(false);
      setSearchVisibleCount(LIST_INITIAL_LIMIT);
      setPlaylistSearch({});
      setPlaylistSearchOpen({});
      setDragOffset(0);
      dragStateRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    setSearchVisibleCount(LIST_INITIAL_LIMIT);
  }, [quickSong]);

  const trackLayoutInteraction = (action: string, metadata?: Record<string, unknown>) => {
    trackFlagInteraction('kb', addSongLayout, action, metadata);
  };

  const showHorizontalCards = !useFocusAwareLayout || isInputFocused;

  useEffect(() => {
    if (!showHorizontalCards && searchVisibleCount < LIST_INITIAL_LIMIT) {
      setSearchVisibleCount(LIST_INITIAL_LIMIT);
    }
  }, [showHorizontalCards, searchVisibleCount]);

  useEffect(() => {
    if (activeTab === 'saved' && !isAuthenticated) {
      setActiveTab('search');
    }
  }, [activeTab, isAuthenticated]);

  useEffect(() => {
    if (open) {
      trackLayoutInteraction('sheet_opened', { activeTab, useFocusAwareLayout });
    }
  }, [open]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: open ? 'auto' : 'none',
        zIndex: 60,
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--color-bg-overlay)',
          opacity: open ? 1 : 0,
          transition: 'opacity 0.25s ease',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: useFocusAwareLayout && isInputFocused ? '70dvh' : expanded ? '95dvh' : '70dvh',
          background: 'var(--surface)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '8px 16px calc(16px + env(safe-area-inset-bottom))',
          transform: open ? `translateY(${dragOffset}px)` : 'translateY(100%)',
          transition: dragOffset !== 0
            ? 'none'
            : 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), max-height 0.3s ease',
          touchAction: 'none',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
        }}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle affordance */}
        <div
          onPointerDown={onDragStart}
          style={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            padding: '6px 0 8px',
            cursor: 'grab',
            touchAction: 'none',
            userSelect: 'none',
          }}
          aria-hidden
        >
          <div style={{ width: 40, height: 4, borderRadius: 999, background: 'var(--border)' }} />
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 4,
              cursor: 'pointer',
              userSelect: 'none',
              touchAction: 'none',
            }}
            onPointerDown={onDragStart}
            onClick={() => {
              if (dragMovedRef.current) {
                dragMovedRef.current = false;
                return;
              }
              setExpanded((v) => !v);
            }}
            title={expanded ? t('search.collapse') : t('search.expand')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{t('search.addSongTitle')}</div>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  color: 'var(--text-muted)',
                }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            <button
              className="btn-secondary"
              style={{
                width: 36,
                height: 36,
                padding: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label={t('search.close')}
              title={t('search.close')}
            >
              ✕
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { key: 'search' as Tab, icon: '🔍', label: t('common.search') },
              { key: 'playlists' as Tab, icon: '☰', label: t('search.playlists') },
              ...(isAuthenticated ? [{ key: 'saved' as Tab, icon: '★', label: t('search.saved') }] : []),
            ].map((tab) => (
              <button
                key={tab.key}
                className={activeTab === tab.key ? 'btn-primary' : 'btn-secondary'}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  fontSize: 18,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
                onClick={() => setActiveTab(tab.key)}
                aria-label={tab.label}
                title={tab.label}
              >
                <span>{tab.icon}</span>
                {!expanded && (
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{tab.label}</span>
                )}
              </button>
            ))}
          </div>

          {activeTab === 'search' && (
            <>
              {typeof singerName === 'string' && setSingerName && (
                <input
                  style={{ width: '100%' }}
                  placeholder={t('search.singerNamePlaceholder')}
                  value={singerName}
                  maxLength={60}
                  onChange={(e) => setSingerName(e.target.value)}
                  autoFocus={open}
                />
              )}

              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  style={{ width: '100%', paddingRight: 44 }}
                  ref={searchInputRef}
                  placeholder={t('search.youtubePlaceholder')}
                  value={quickSong}
                  maxLength={200}
                  onChange={(e) => setQuickSong(e.target.value)}
                  onFocus={() => setIsInputFocused(true)}
                  onBlur={() => setIsInputFocused(false)}
                  autoFocus={typeof singerName !== 'string'}
                />
                {quickSong.trim() && (
                  <button
                    onClick={() => setQuickSong('')}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-muted)',
                      fontSize: 18,
                      padding: '6px 8px',
                      cursor: 'pointer',
                    }}
                    aria-label={t('search.clearSearch')}
                    title={t('search.clearSearch')}
                  >
                    ✕
                  </button>
                )}
              </div>

              {ytError && (
                <div className="search-error">
                  {ytError}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
          {activeTab === 'search' && selectedArtist && onClearArtist && (
            <>
              <div className="artist-view__header">
                <button
                  type="button"
                  className="artist-view__back"
                  onClick={onClearArtist}
                >
                  ← {t('common.back')}
                </button>
                <div className="artist-view__title">{selectedArtist}</div>
              </div>
              {isLoadingArtistSongs && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="yt-skeleton" style={{ height: 90, borderRadius: 12 }} />
                  ))}
                </div>
              )}
              {!isLoadingArtistSongs && artistSongs && artistSongs.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                  {artistSongs.map((video) => {
                    const isFav = favorites.some((f) => f.videoId === video.videoId);
                    return (
                      <VideoCardHorizontal
                        key={video.videoId}
                        video={video}
                        isAuthenticated={isAuthenticated}
                        isFav={isFav}
                        onToggleFavorite={onToggleFavorite}
                        onPreview={onPreview}
                        onAdd={onAdd}
                        isAdding={isAdding}
                      />
                    );
                  })}
                </div>
              )}
              {!isLoadingArtistSongs && (!artistSongs || artistSongs.length === 0) && (
                <div style={{ marginTop: 4, color: 'var(--text-muted)', fontSize: 13 }}>
                  {t('search.noResults')}
                </div>
              )}
            </>
          )}

          {activeTab === 'search' && !selectedArtist && (
            <>
              {/* Sem pesquisa (ou sem resultados): artistas no topo. */}
              {ytResults.length === 0 && artistRow}

              {!isSearching && !ytError && ytResults.length === 0 && quickSong.trim().length > 3 && (
                <div style={{ marginTop: 4, color: 'var(--text-muted)', fontSize: 13 }}>
                  {t('search.noResults')}
                </div>
              )}

              {isAuthenticated && favorites.length > 0 && !isSearching && ytResults.length === 0 && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>{t('search.yourFavorites')}</div>
                  {showHorizontalCards ? (
                    <div
                      style={{
                        display: 'flex',
                        gap: 12,
                        overflowX: 'auto',
                        paddingBottom: 6,
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                      {favorites.map((video) => (
                        <VideoCard
                          key={`fav-${video.videoId}`}
                          video={video}
                          isAuthenticated={isAuthenticated}
                          isFav
                          onToggleFavorite={onToggleFavorite}
                          onPreview={onPreview}
                          onAdd={onAdd}
                          compact
                          isAdding={isAdding}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {favorites.map((video) => (
                        <VideoCardHorizontal
                          key={`fav-${video.videoId}`}
                          video={video}
                          isAuthenticated={isAuthenticated}
                          isFav
                          onToggleFavorite={onToggleFavorite}
                          onPreview={onPreview}
                          onAdd={onAdd}
                          compact
                          isAdding={isAdding}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {isSearching && (
                <>
                  {showHorizontalCards ? (
                    <div
                      style={{
                        display: 'flex',
                        gap: 12,
                        overflowX: 'auto',
                        paddingBottom: 6,
                        marginTop: 4,
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="yt-skeleton"
                          style={{
                            flexShrink: 0,
                            width: 'min(72vw, 220px)',
                            background: 'var(--bg)',
                            border: '1px solid var(--border)',
                            borderRadius: 12,
                            overflow: 'hidden',
                            scrollSnapAlign: 'start',
                          }}
                        >
                          <div className="skeleton-thumb" style={{ width: '100%', paddingTop: '56.25%', position: 'relative' }} />
                          <div style={{ padding: 12 }}>
                            <div className="skeleton-text" style={{ height: 40, marginBottom: 10, borderRadius: 6 }} />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <div className="skeleton-btn" style={{ flex: 1, height: 36, borderRadius: 10 }} />
                              <div className="skeleton-btn" style={{ flex: 1, height: 36, borderRadius: 10 }} />
                              <div className="skeleton-btn" style={{ flex: 1, height: 36, borderRadius: 10 }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div
                          key={i}
                          className="yt-skeleton"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            padding: 16,
                            background: 'var(--bg)',
                            border: '1px solid var(--border)',
                            borderRadius: 12,
                          }}
                        >
                          <div className="skeleton-thumb" style={{ width: 160, height: 90, borderRadius: 8, flexShrink: 0 }} />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div className="skeleton-text" style={{ height: 16, width: '80%', borderRadius: 6 }} />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <div className="skeleton-btn" style={{ width: 44, height: 44, borderRadius: 10 }} />
                              <div className="skeleton-btn" style={{ width: 44, height: 44, borderRadius: 10 }} />
                              <div className="skeleton-btn" style={{ width: 44, height: 44, borderRadius: 10 }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {!isSearching && ytResults.length > 0 && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
                    {ytResults.map((video) => {
                      const isFav = favorites.some((f) => f.videoId === video.videoId);
                      return (
                        <VideoCardHorizontal
                          key={video.videoId}
                          video={video}
                          isAuthenticated={isAuthenticated}
                          isFav={isFav}
                          onToggleFavorite={onToggleFavorite}
                          onPreview={onPreview}
                          onAdd={onAdd}
                          isAdding={isAdding}
                        />
                      );
                    })}
                  </div>
                  {/* Com resultados: artistas no fim da lista, antes do botão
                      de pesquisa forçada no YouTube. */}
                  {artistRow}
                  {resultsFromCache && !forceDisabled && !isSearching && ytResults.length > 0 && quickSong.trim().length > 0 && (
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ width: '100%', marginTop: 8 }}
                      onClick={onForceYouTubeSearch}
                    >
                      {t('search.forceYouTube')}
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'playlists' && (
            <div className="add-song-playlist" style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 4 }}>
              {playlists.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>
                  {t('search.noPlaylists')}
                </div>
              ) : showHorizontalCards ? (
                playlists.map((playlist) => {
                  const playlistSongs = getPlaylistSongs(playlist);
                  return (
                    <div key={playlist.id} className="panel-card panel-card--tight">
                      <PlaylistHeader
                        playlist={playlist}
                        searchOpen={!!playlistSearchOpen[playlist.id]}
                        searchQuery={playlistSearch[playlist.id] || ''}
                        onToggle={() =>
                          setPlaylistSearchOpen((prev) => ({
                            ...prev,
                            [playlist.id]: !prev[playlist.id],
                          }))
                        }
                        onChange={(value) =>
                          setPlaylistSearch((prev) => ({
                            ...prev,
                            [playlist.id]: value,
                          }))
                        }
                        t={t}
                      />
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          overflowX: 'auto',
                          paddingBottom: 6,
                          scrollSnapType: 'x mandatory',
                          WebkitOverflowScrolling: 'touch',
                        }}
                      >
                        {playlistSongs.map((video) => (
                          <VideoCard
                            key={video.videoId}
                            video={video}
                            isAuthenticated={isAuthenticated}
                            isFav={favorites.some((f) => f.videoId === video.videoId)}
                            onToggleFavorite={onToggleFavorite}
                            onPreview={onPreview}
                            onAdd={onAdd}
                            compact
                            isAdding={isAdding}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ position: 'relative' }}>
                  {playlists.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 12 }}>
                      {playlists.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setPlaylistCarouselIndex(idx);
                            const carousel = playlistCarouselRef.current;
                            if (!carousel) return;
                            const firstCard = carousel.firstElementChild as HTMLElement | null;
                            const cardWidth = firstCard ? firstCard.offsetWidth : carousel.offsetWidth * 0.85;
                            const gap = 12;
                            carousel.scrollTo({ left: idx * (cardWidth + gap), behavior: 'smooth' });
                          }}
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            border: 'none',
                            padding: 0,
                            background: idx === playlistCarouselIndex ? 'var(--accent)' : 'var(--border)',
                            cursor: 'pointer',
                          }}
                          aria-label={t('search.goToPlaylist', { n: idx + 1 })}
                        />
                      ))}
                    </div>
                  )}
                  <div
                    ref={playlistCarouselRef}
                    style={{
                      display: 'flex',
                      gap: 12,
                      overflowX: 'auto',
                      scrollSnapType: 'x mandatory',
                      scrollBehavior: 'smooth',
                      WebkitOverflowScrolling: 'touch',
                      paddingBottom: 8,
                    }}
                    onScroll={() => {
                      const carousel = playlistCarouselRef.current;
                      if (!carousel) return;
                      const firstCard = carousel.firstElementChild as HTMLElement | null;
                      const cardWidth = firstCard ? firstCard.offsetWidth : carousel.offsetWidth * 0.92;
                      const gap = 12;
                      const newIndex = Math.round(carousel.scrollLeft / (cardWidth + gap));
                      setPlaylistCarouselIndex(Math.max(0, Math.min(playlists.length - 1, newIndex)));
                    }}
                  >
                    {playlists.map((playlist) => {
                      const playlistSongs = getPlaylistSongs(playlist);
                      return (
                        <div
                          key={playlist.id}
                          className="panel-card panel-card--tight"
                          style={{
                            flex: '0 0 calc(92% - 16px)',
                            scrollSnapAlign: 'start',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                          }}
                        >
                          <PlaylistHeader
                            playlist={playlist}
                            searchOpen={!!playlistSearchOpen[playlist.id]}
                            searchQuery={playlistSearch[playlist.id] || ''}
                            onToggle={() =>
                              setPlaylistSearchOpen((prev) => ({
                                ...prev,
                                [playlist.id]: !prev[playlist.id],
                              }))
                            }
                            onChange={(value) =>
                              setPlaylistSearch((prev) => ({
                                ...prev,
                                [playlist.id]: value,
                              }))
                            }
                            t={t}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {playlistSongs.map((video) => (
                              <VideoCardHorizontal
                                key={video.videoId}
                                video={video}
                                isAuthenticated={isAuthenticated}
                                isFav={favorites.some((f) => f.videoId === video.videoId)}
                                onToggleFavorite={onToggleFavorite}
                                onPreview={onPreview}
                                onAdd={onAdd}
                                isAdding={isAdding}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'saved' && (
            <div style={{ marginTop: 4 }}>
              {savedSongs.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>
                  {t('search.noSavedSongs')}
                </div>
              ) : showHorizontalCards ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    overflowX: 'auto',
                    paddingBottom: 6,
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {savedSongs.map((video) => {
                    const isFav = favorites.some((f) => f.videoId === video.videoId);
                    return (
                      <VideoCard
                        key={`saved-${video.videoId}`}
                        video={video}
                        isAuthenticated={isAuthenticated}
                        isFav={isFav}
                        onToggleFavorite={onToggleFavorite}
                        onPreview={onPreview}
                        onAdd={onAdd}
                        compact
                        isAdding={isAdding}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="panel-card panel-card--tight" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {savedSongs.map((video) => {
                    const isFav = favorites.some((f) => f.videoId === video.videoId);
                    return (
                      <VideoCardHorizontal
                        key={`saved-${video.videoId}`}
                        video={video}
                        isAuthenticated={isAuthenticated}
                        isFav={isFav}
                        onToggleFavorite={onToggleFavorite}
                        onPreview={onPreview}
                        onAdd={onAdd}
                        isAdding={isAdding}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
